// src/components/Sections/Admin/Roles/RolForm.jsx
//
// Crear y editar un rol. La misma pantalla sirve para los dos casos y para la lectura
// del rol de administrador, que no se puede modificar.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Form, Input, Spin, Switch, App } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { FiArrowLeft, FiShield, FiLock, FiUsers, FiKey, FiSave, FiInfo } from 'react-icons/fi';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Roles from '../../../../service/Roles';
import { useNotify } from '../../../../providers/NotificationProvider.jsx';
import useAuthContext from '../../../../hooks/useAuthContext';
import { can, permisosVacios } from '../../../../config/rbac';
import MatrizPermisos from './MatrizPermisos';
import { claseBtnPrimario, claseBtnSecundario } from '../../../Ui/Admin/PiezasAdmin';

/** La clave es la que viaja en el token; una vez creada no se cambia. */
const CLAVE_VALIDA = /^[a-z][a-z0-9_]*$/;

const Tarjeta = ({ children, className = '' }) => (
  <section className={`rounded-3xl bg-white border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,.04),0_8px_24px_-14px_rgba(0,0,0,.10)] ${className}`}>
    {children}
  </section>
);

const RolForm = () => {
  const { key } = useParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const { message } = App.useApp();
  const { user } = useAuthContext();
  const [form] = Form.useForm();

  const esNuevo = !key;
  const [modulos, setModulos] = useState([]);
  const [permisos, setPermisos] = useState(permisosVacios());
  const [permisosIniciales, setPermisosIniciales] = useState(permisosVacios());
  const [datosTocados, setDatosTocados] = useState(false);
  const [rol, setRol] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  // El rol de administrador se muestra en solo lectura: el servidor rechaza cambiarlo
  // y es preferible que la pantalla lo diga antes que enterarse al guardar.
  const esAdmin = key === 'admin';
  const soloLectura = esAdmin || !can(user, 'roles', 'editar');

  const cargar = useCallback(async () => {
    try {
      setCargando(true);
      setError(null);
      setDatosTocados(false);

      if (esNuevo) {
        const catalogo = await Roles.modulos();
        setModulos(catalogo);
        setPermisos(permisosVacios());
        setPermisosIniciales(permisosVacios());
        // Limpiar a mano: `/admin/roles/:key` y `/admin/roles/nuevo` renderizan este
        // mismo componente, así que React lo reusa en vez de montarlo de nuevo. Sin
        // esto, venir desde la ficha de un rol del sistema dejaba el formulario nuevo
        // con su chapa de "Sistema" y el aviso de que no se puede eliminar.
        setRol(null);
        form.resetFields();
      } else {
        const data = await Roles.obtener(key);
        if (!data) throw new Error('No existe ese rol');
        setRol(data);
        setModulos(data.modulos || []);
        setPermisos(data.permisos || permisosVacios());
        setPermisosIniciales(data.permisos || permisosVacios());
        form.setFieldsValue({
          nombre: data.nombre,
          descripcion: data.descripcion || '',
          accesoTorniquete: data.accesoTorniquete === true,
        });
      }
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'No se pudo cargar el rol.');
    } finally {
      setCargando(false);
    }
  }, [esNuevo, key, form]);

  useEffect(() => { cargar(); }, [cargar]);

  const resumen = useMemo(() => {
    const v = Object.values(permisos || {});
    return {
      ve: v.filter(p => p.ver).length,
      edita: v.filter(p => p.editar).length,
      conAprobacion: v.filter(p => p.editar && p.requiereAprobacion).length,
      total: modulos.length || v.length,
    };
  }, [permisos, modulos.length]);

  const hayCambios = datosTocados || JSON.stringify(permisos) !== JSON.stringify(permisosIniciales);

  const guardar = async valores => {
    // Un rol que no ve nada y tampoco abre el torniquete no le sirve a nadie. No se
    // bloquea —puede ser un rol que se está armando— pero sí se avisa.
    const veAlgo = Object.values(permisos).some(p => p.ver);
    if (!veAlgo && !valores.accesoTorniquete) {
      message.warning('Este rol no da acceso a ninguna pantalla ni al torniquete.');
    }

    try {
      setGuardando(true);
      const payload = {
        nombre: valores.nombre?.trim(),
        descripcion: valores.descripcion?.trim() || undefined,
        accesoTorniquete: valores.accesoTorniquete === true,
        permisos,
      };

      if (esNuevo) {
        await Roles.crear({ ...payload, key: valores.key.trim().toLowerCase() });
        notify.success('Rol creado');
      } else {
        await Roles.actualizar(key, payload);
        notify.success('Rol actualizado');
      }
      navigate('/admin/roles');
    } catch (e) {
      // El 422 de coherencia trae el detalle de qué módulo está mal: se muestra tal
      // cual, porque decir "error al guardar" obligaría a adivinar cuál casilla.
      notify.error(e?.response?.data?.message || 'No se pudo guardar el rol.');
    } finally {
      setGuardando(false);
    }
  };

  if (cargando) {
    return <div className="py-24 text-center"><Spin size="large" /></div>;
  }

  const personas = rol?.usuarios;

  return (
    <div className="max-w-[1280px]">
      <Link to="/admin/roles" className="inline-flex items-center gap-1.5 mb-4 text-sm font-semibold text-gray-500 hover:!text-primary transition-colors">
        <FiArrowLeft size={15} /> Roles y permisos
      </Link>

      {/* ── Cabecera ── */}
      <Tarjeta className="p-6 mb-5">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
          <div className="flex items-start gap-4 min-w-0">
            <span className="relative w-14 h-14 flex-shrink-0 rounded-2xl bg-primary text-white flex items-center justify-center">
              <FiShield size={24} />
              <span aria-hidden className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-secondary ring-4 ring-white" />
            </span>
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 leading-tight m-0">
                {esNuevo ? <>Nuevo <span className="hl-amarillo">rol</span></> : rol?.nombre}
              </h1>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {rol?.sistema && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-bold">
                    <FiLock size={11} /> Rol del sistema
                  </span>
                )}
                {rol?.key && <code className="px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 text-xs">{rol.key}</code>}
                {typeof personas === 'number' && (
                  <span className="inline-flex items-center gap-1 text-sm text-gray-600">
                    <FiUsers size={13} /> {personas.toLocaleString('es-CL')} {personas === 1 ? 'persona' : 'personas'}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Resumen en vivo de la matriz */}
          <div className="grid grid-cols-3 gap-2 flex-shrink-0">
            {[
              { n: resumen.ve, label: 've' },
              { n: resumen.edita, label: 'edita' },
              { n: resumen.conAprobacion, label: 'con aprobación' },
            ].map(s => (
              <div key={s.label} className="px-4 py-2.5 rounded-2xl bg-gray-50 text-center min-w-[92px]">
                <p className="text-xl font-extrabold text-gray-900 leading-none m-0 tabular-nums">
                  {s.n}<span className="text-sm font-bold text-gray-400">/{resumen.total}</span>
                </p>
                <p className="text-[11px] font-semibold text-gray-500 m-0 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Un solo aviso según el caso (antes eran dos alertas grandes) */}
        {(esAdmin || rol?.sistema || (typeof personas === 'number' && personas > 0 && !soloLectura)) && (
          <div className={`flex items-start gap-3 mt-5 p-4 rounded-2xl text-sm ${esAdmin ? 'bg-blue-50 text-[#00266b]' : 'bg-[#fffbea] border border-[#fde68a] text-[#5c4300]'}`}>
            <FiInfo size={16} className="mt-0.5 flex-shrink-0" />
            <span>
              {esAdmin
                ? 'Los permisos del administrador no se pueden modificar: es la salida de emergencia del sistema. Si se le pudiera quitar un permiso por error, no quedaría nadie capaz de devolverlo.'
                : <>
                  {rol?.sistema && 'Rol del sistema: se le pueden cambiar los permisos, pero no eliminar. '}
                  {typeof personas === 'number' && personas > 0 && <>El cambio afecta a <strong>{personas.toLocaleString('es-CL')} {personas === 1 ? 'persona' : 'personas'}</strong> apenas guardes.</>}
                </>}
            </span>
          </div>
        )}
      </Tarjeta>

      {error && <Alert type="error" showIcon className="mb-4" message={error} />}

      <Form form={form} layout="vertical" onFinish={guardar} disabled={soloLectura}
        className="form-compacto" onValuesChange={() => setDatosTocados(true)}>
        <div className="grid grid-cols-1 xl:grid-cols-[360px_1fr] gap-5 items-start">
          {/* ── Datos del rol ── */}
          <div className="space-y-5 xl:sticky xl:top-4">
            <Tarjeta className="p-5">
              <h2 className="text-sm font-extrabold uppercase tracking-widest text-gray-900 mb-4">Datos del rol</h2>
              {esNuevo && (
                <Form.Item
                  name="key"
                  label="Clave"
                  tooltip="Identificador interno del rol. Viaja en la sesión de cada persona y no se puede cambiar después."
                  rules={[
                    { required: true, message: 'La clave es obligatoria' },
                    { pattern: CLAVE_VALIDA, message: 'Solo minúsculas, números y guion bajo, empezando por una letra' },
                    { max: 40, message: 'Máximo 40 caracteres' },
                  ]}
                >
                  <Input placeholder="por ejemplo: funcionario" />
                </Form.Item>
              )}
              <Form.Item
                name="nombre"
                label="Nombre"
                rules={[
                  { required: true, message: 'El nombre es obligatorio' },
                  { min: 2, message: 'Mínimo 2 caracteres' },
                  { max: 80, message: 'Máximo 80 caracteres' },
                ]}
              >
                <Input placeholder="Funcionario" />
              </Form.Item>
              <Form.Item name="descripcion" label="Descripción" className="!mb-0" rules={[{ max: 300, message: 'Máximo 300 caracteres' }]}>
                <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} placeholder="Para qué sirve este rol y quién debería tenerlo" />
              </Form.Item>
            </Tarjeta>

            <Tarjeta className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <span className="w-9 h-9 flex-shrink-0 rounded-xl bg-secondary/40 text-[#5c4300] flex items-center justify-center">
                    <FiKey size={16} />
                  </span>
                  <div>
                    <p className="text-sm font-bold text-gray-900 m-0">Acceso al torniquete</p>
                    <p className="text-xs text-gray-500 m-0 mt-1 leading-relaxed">
                      Abre la puerta del recinto con su cédula, sin depender de una inscripción ni de un bloque horario.
                    </p>
                  </div>
                </div>
                <Form.Item name="accesoTorniquete" valuePropName="checked" className="!mb-0">
                  <Switch disabled={soloLectura || esAdmin} aria-label="Acceso al torniquete" />
                </Form.Item>
              </div>
              <p className="text-xs text-gray-500 mt-3 mb-0 pt-3 border-t border-gray-100">
                Se escribe en el equipo del recinto en la próxima corrida diaria. Si se le quita a un rol, esa misma corrida expira la ventana de quienes lo tengan.
              </p>
            </Tarjeta>
          </div>

          {/* ── Permisos ── */}
          <Tarjeta className="overflow-hidden">
            <header className="px-6 pt-5 pb-3">
              <h2 className="text-lg font-extrabold text-gray-900 m-0">Permisos <span className="hl-amarillo">por módulo</span></h2>
              <p className="text-sm text-gray-500 m-0 mt-1">Qué puede ver y hacer este rol en cada parte del panel.</p>
            </header>
            <div className="px-2 pb-3">
              <MatrizPermisos modulos={modulos} permisos={permisos} onChange={setPermisos} soloLectura={soloLectura} />
            </div>
          </Tarjeta>
        </div>

        {/* ── Barra fija para guardar ── */}
        {!soloLectura && (
          <div className="sticky bottom-4 z-30 mt-5">
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 rounded-2xl bg-[#00266b] text-white shadow-[0_16px_40px_-12px_rgba(0,38,107,.6)]">
              <span className="flex items-center gap-2 text-sm font-semibold">
                <span className={`w-2 h-2 rounded-full ${hayCambios ? 'bg-secondary' : 'bg-blue-300'}`} />
                {hayCambios ? 'Tienes cambios sin guardar' : 'Sin cambios'}
              </span>
              <div className="flex gap-2">
                <button type="button" onClick={() => navigate('/admin/roles')}
                  className={`${claseBtnSecundario} !bg-transparent !border-white/30 !text-white hover:!border-white`}>
                  Cancelar
                </button>
                <button type="submit" disabled={guardando || (!esNuevo && !hayCambios)}
                  className={`${claseBtnPrimario} !bg-secondary !text-[#00266b] disabled:!opacity-50`}>
                  {guardando ? <LoadingOutlined /> : <FiSave size={15} />} {esNuevo ? 'Crear rol' : 'Guardar cambios'}
                </button>
              </div>
            </div>
          </div>
        )}
      </Form>
    </div>
  );
};

export default RolForm;
